import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { PreviewCollage } from './PreviewCollage';

export interface PersonTileProps {
  id: string;
  name: string;
  emoji: string;
  /** Up to 4 image URLs. Empty + `loading=false` triggers the emoji fallback. */
  previewImages?: string[];
  /** True until the first preview snapshot resolves. Renders the shimmer block. */
  loading?: boolean;
  isMe?: boolean;
  onClick?: () => void;
  /** Destination route. When provided, the tile renders as a react-router
   *  Link so the browser handles Cmd/Ctrl/Shift-click natively (open in
   *  new tab / new window). When omitted, falls back to a button + onClick.
   *  Kate feedback 2026-05-13: "Shift+Click (or Ctrl/Cmd+Click) is a
   *  standard, native browser UX pattern to open a link in a new tab.
   *  Today this doesn't happen — it should." */
  href?: string;
}

const tileBaseStyles = css`
  display: flex;
  flex-direction: column;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 16px;
  color: inherit;
  text-decoration: none;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px hsl(var(--primary));
  }
`;

const TileButton = styled.button`
  ${tileBaseStyles}
`;

const TileLink = styled(Link)`
  ${tileBaseStyles}
`;

const TileFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 16px;
  overflow: hidden;
  background: hsl(var(--muted));
  border: 1px solid #e5e0d8;
`;
// AvatarBadge retired 2026-05-13 — Kate's call: "remove the extra emoji
// floating like an avatar on top of my people's boards. emojis should
// just be next to the person's name (as they are currently)." The emoji
// still renders in TileLabel below the collage.

const TileLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
  @media (min-width: 640px) {
    margin-top: 12px;
  }
`;

const LabelEmoji = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const LabelName = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: hsl(var(--foreground));
  @media (min-width: 640px) {
    font-size: 16px;
  }
`;

export const PersonTile: React.FC<PersonTileProps> = ({
  name,
  emoji,
  previewImages = [],
  loading = false,
  isMe = false,
  onClick,
  href,
}) => {
  const ariaLabel = isMe ? 'Open your closet' : `Open ${name}'s closet`;
  const inner = (
    <>
      <TileFrame>
        <PreviewCollage images={previewImages} fallbackEmoji={emoji} loading={loading} />
      </TileFrame>
      <TileLabel>
        <LabelEmoji aria-hidden>{emoji}</LabelEmoji>
        <LabelName>{name}</LabelName>
      </TileLabel>
    </>
  );
  if (href) {
    return (
      <TileLink to={href} aria-label={ariaLabel} onClick={onClick}>
        {inner}
      </TileLink>
    );
  }
  return (
    <TileButton type="button" onClick={onClick} aria-label={ariaLabel}>
      {inner}
    </TileButton>
  );
};
